import { useTranslation } from "../../localization/useTranslation";
import { useUrlProvider } from "../../useUrlProvider";
import { useNavigate, useParams } from "react-router";
import { SystemOverviewInfoViewModel, SystemPlanTabId } from "./SystemOverview.types";
import { useOverviewTopTab } from "@common/hooks/useOverviewTopTab";
import { createElement, useState } from "react";
import { UserPermissions } from "../../auth/userContextProvider/UserContextProvider.types";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useLocalStorageTab } from "@common/hooks/useLocalStorageTab";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";

import { useDotLegalQuery, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

export function useSystemOverviewHook() {
    const navigate = useNavigate();
    const { getSystemsUrl, getSystemEditUrl } = useUrlProvider();
    const { translateString } = useTranslation();
    const { customerId } = useUserContext();

    const [showDeleteSystemDialog, setShowDeleteSystemDialog] = useState(false);
    const [showEditSystemTemplateInfo, setShowEditSystemTemplateInfo] = useState(false);
    const [showInternalControlProcedureDialog, setShowInternalControlProcedureDialog] = useState(false);
    const [showRiskAreasDialog, setShowRiskAreasDialog] = useState(false);

    const [selectedPlanTab, setSelectedPlanTab] = useLocalStorageTab(`system-plan-tab-${customerId}`, systemPlanTabMapping, SystemPlanTabId.Documents);

    const { id } = useParams<{ id: string }>();

    const queryKey = "systemOverview" + id;

    const infoQuery = useDotLegalQueryWithKey<SystemOverviewInfoViewModel>(`${queryKey}`, `/SystemOverview/${id}/info`);

    let infoLoading = infoQuery.isLoading;
    let infoData = infoQuery.data;

    const { selectedTopTab, setSelectedTopTab } = useOverviewTopTab("system-info-tab", "allSystemInfo", infoData?.entityTemplateId, infoData?.templateSteps);

    const deleteSystemSuccess = () => {
        navigate(getSystemsUrl());
    };

    const getBreadCrumbs = () => {
        return [
            {
                name: translateString("systems"),
                link: getSystemsUrl(),
            },
            {
                name: infoData ? `${infoData?.name} (${infoData?.userFriendlyId})` : undefined,
                tooltip: isNullOrWhitespace(infoData?.description)
                    ? undefined
                    : createElement(MentionInputRender, { value: infoData?.description ?? "", linkColor: "white" }),
            },
        ];
    };

    const refetchSystemInfo = () => {
        infoQuery.refetch();
    };

    return {
        infoLoading,
        infoData,
        getBreadCrumbs,
        getSystemsUrl,
        editUrl: getSystemEditUrl(id),
        showDeleteSystemDialog,
        setShowDeleteSystemDialog,
        deleteSystemSuccess,
        showEditSystemTemplateInfo,
        setShowEditSystemTemplateInfo,
        id,
        selectedTab: selectedPlanTab,
        setSelectedTab: setSelectedPlanTab,
        selectedTopTab,
        setSelectedTopTab,
        showInternalControlProcedureDialog,
        setShowInternalControlProcedureDialog,
        showRiskAreasDialog,
        setShowRiskAreasDialog,
        refetchSystemInfo,
    };
}

const systemPlanTabMapping: Record<number, SystemPlanTabId> = {
    0: SystemPlanTabId.Documents,
    1: SystemPlanTabId.Suppliers,
    2: SystemPlanTabId.Access,
    3: SystemPlanTabId.ProcessingActivities,
    4: SystemPlanTabId.Audits,
    5: SystemPlanTabId.Classifications,
    6: SystemPlanTabId.Tasks,
};
